// Thin wrapper sobre fetch. Centraliza la base URL (env-driven), maneja
// errores y devuelve tipos. Sin librerías externas — fetch nativo es
// suficiente para un read-only API.

import { filtersToParams, type Filters } from './filters';
import type {
  AccessDelivery,
  AdminUser,
  AuthUser,
  CrimeResponse,
  LegalStatus,
  FacetsResponse,
  HealthDeepResponse,
  HealthV1Response,
  HeatmapResponse,
  PlacesResponse,
  PropertyCollection,
  PropertyDetail,
  PropertyListResponse,
  AccessRequest,
  StatsResponse,
  WatchlistItem,
  SocioAgebResponse,
  Organization,
  AuditEntry,
  OrganizationSeats,
  UsageSummary,
  ApiKey,
  MyOrganization,
  PlansResponse,
  PlanRules,
  OrganizationDetail,
  SuperMetrics,
  PublicPlan,
  PlatformSettings,
  TrialSignupMode,
} from './types';

// `.trim()` + quitar la diagonal final: medido en producción, la variable
// de entorno de Vercel traía un SALTO DE LÍNEA al final y todas las URLs
// salían como `https://…app\n/v1/stats`. Los navegadores lo toleran, pero
// cualquier consumidor estricto (o un log) lo rompe.
const BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:3000').trim().replace(/\/+$/, '');

/** Tope por request: sin esto, un request colgado dejaba "cargando…" eterno. */
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * Combina el signal del caller con un timeout propio. Devuelve el signal
 * compuesto + un cleanup + un flag para distinguir "venció el timeout" de
 * "el caller abortó" (los dos llegan como AbortError del fetch).
 */
function withTimeout(signal: AbortSignal | undefined, ms = REQUEST_TIMEOUT_MS): {
  signal: AbortSignal;
  cleanup: () => void;
  timedOut: () => boolean;
} {
  const ctrl = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => ctrl.abort();
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener('abort', onCallerAbort, { once: true });
  }
  const timer = window.setTimeout(() => { timedOut = true; ctrl.abort(); }, ms);
  return {
    signal: ctrl.signal,
    cleanup: () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onCallerAbort);
    },
    timedOut: () => timedOut,
  };
}

const TIMEOUT_MSG = 'La solicitud tardó demasiado. Revisa tu conexión e intenta de nuevo.';

/** Mensaje humano por status — esto termina como copy visible en la UI. */
function humanHttpError(status: number): string {
  if (status >= 500) return `El servidor tuvo un problema (HTTP ${status}). Intenta de nuevo en un momento.`;
  if (status === 429) return 'Demasiadas solicitudes seguidas — espera un momento.';
  return `No se pudo completar la solicitud (HTTP ${status}).`;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const url = `${BASE}${path}`;
  const t = withTimeout(signal);
  try {
    // `credentials: 'include'` SIEMPRE: la lista, el mapa autenticado y la
    // ficha (RFC-0021 Bloque C) exigen la cookie de sesión, y el API vive
    // en otro ORIGEN (api.terrakairos.mx ≠ app.terrakairos.mx). Sin esto
    // el navegador no manda la cookie y esas rutas devolvían 401 aun con
    // sesión iniciada — la ficha pedía "iniciar sesión" en un ciclo sin
    // fin (medido en producción 2026-09-17). En rutas públicas es inocuo:
    // CORS responde con el origen exacto y allow-credentials.
    const res = await fetch(url, { signal: t.signal, credentials: 'include' });
    if (!res.ok) {
      // Los 401/403 traen un código de máquina útil (unauthorized,
      // quota_exceeded, plan_expired, feature_not_in_plan…) y la UI decide
      // qué decir con él. El resto se humaniza: antes `GET /v1/x → HTTP
      // 500` terminaba pintado tal cual como copy de error.
      if (res.status === 401 || res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const code = typeof body.error === 'string' ? body.error : humanHttpError(res.status);
        throw new ApiError(res.status, code, body);
      }
      throw new ApiError(res.status, humanHttpError(res.status));
    }
    return (await res.json()) as T;
  } catch (err) {
    if (t.timedOut()) throw new Error(TIMEOUT_MSG);
    throw err;
  } finally {
    t.cleanup();
  }
}

/**
 * Caché en memoria con dedup de requests en vuelo (RFC-0021 A3).
 *
 * Problema medido: /v1/health se pedía 2× (HealthBadge + HealthDashboard,
 * cada uno con su interval), /v1/facets 2× por cambio de filtros (App +
 * FilterBar) y StrictMode duplica efectos en dev. Con esta caché, llamadas
 * al MISMO path dentro del TTL comparten UNA promesa/request.
 *
 * Nota: las promesas cacheadas NO se atan al AbortSignal de un caller
 * (abortar la request compartida rompería al otro consumidor). Los
 * callers ya se protegen con su flag `cancelled` al desmontar.
 */
const requestCache = new Map<string, { at: number; promise: Promise<unknown> }>();

/** Vacía la caché de requests: al entrar o salir de una cuenta, lo cacheado era de otra sesión. */
export function clearRequestCache(): void {
  requestCache.clear();
}

function cachedGet<T>(path: string, ttlMs: number, allowDegraded = false): Promise<T> {
  const hit = requestCache.get(path);
  const now = Date.now();
  if (hit && now - hit.at < ttlMs) return hit.promise as Promise<T>;
  // Poda suave: los details de propiedades acumulan una entrada por id.
  if (requestCache.size > 100) {
    for (const [k, v] of requestCache) {
      if (now - v.at > 60_000) requestCache.delete(k);
    }
  }
  const promise = (allowDegraded ? getAllowingDegraded<T>(path) : get<T>(path)).catch((err) => {
    // No cachear fallos: el siguiente caller reintenta.
    requestCache.delete(path);
    throw err;
  });
  requestCache.set(path, { at: now, promise });
  return promise;
}

// /health/deep responde con 503 cuando hay adapters stale o DB caída,
// PERO el body sigue siendo JSON válido con el detalle. Eso es semántica
// correcta para uptime monitors. Acá leemos el payload sin importar el
// status code — el detalle (`ok`, `db`, `adapters`) ya dice todo.
async function getAllowingDegraded<T>(path: string, signal?: AbortSignal): Promise<T> {
  const url = `${BASE}${path}`;
  const t = withTimeout(signal);
  try {
    const res = await fetch(url, { signal: t.signal, credentials: 'include' });
    // Solo fallamos en errores de red (fetch ya lanzó) o respuestas no-JSON
    // (5xx sin body). Cualquier 200/4xx/5xx con JSON válido se devuelve.
    return (await res.json()) as T;
  } catch (err) {
    if (t.timedOut()) throw new Error(TIMEOUT_MSG);
    throw err;
  } finally {
    t.cleanup();
  }
}

/** Error de API con status, para que el caller distinga 401 de 4xx/5xx. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, public body: Record<string, unknown> = {}) {
    // `code` puede ser un código de máquina (rutas auth) o un mensaje
    // humano (get). El message siempre es mostrable. `body` trae el
    // detalle (p. ej. used/limit de un cupo agotado, RFC-0025).
    super(code);
  }
}

/**
 * fetch con cookie de sesión (RFC-0020). `credentials: 'include'` para que
 * el navegador mande la cookie httpOnly cross-site. Lanza ApiError con el
 * código del body en respuestas no-OK. Con timeout propio: los requests de
 * auth/watchlist/admin tampoco deben poder colgarse para siempre.
 */
async function authFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const t = withTimeout(undefined);
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      signal: t.signal,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string };
    if (!res.ok) {
      throw new ApiError(res.status, typeof body.error === 'string' ? body.error : `http_${res.status}`, body);
    }
    return body as T;
  } catch (err) {
    if (t.timedOut()) throw new Error(TIMEOUT_MSG);
    throw err;
  } finally {
    t.cleanup();
  }
}

export const api = {
  base: BASE,
  // stats/health/facets van por la caché compartida (A3): el signal del
  // caller se acepta por compat pero la request compartida no se aborta.
  stats: (_signal?: AbortSignal) => cachedGet<StatsResponse>('/v1/stats', 60_000),
  healthDeep: (signal?: AbortSignal) => getAllowingDegraded<HealthDeepResponse>('/health/deep', signal),
  /** TK-S02-04: health con status clasificado (OK/DEGRADED/STALE/ERROR) por adapter. */
  health: (_signal?: AbortSignal) => cachedGet<HealthV1Response>('/v1/health', 55_000, true),
  propertiesGeoJson: (params: { limit?: number; bbox?: string; filters?: Filters } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.bbox) q.set('bbox', params.bbox);
    if (params.filters) {
      for (const [k, v] of Object.entries(filtersToParams(params.filters))) q.set(k, v);
    }
    const qs = q.toString();
    return get<PropertyCollection>(`/v1/properties/geojson${qs ? '?' + qs : ''}`, signal);
  },
  // TTL corto: dedupa el doble fetch del deep-link (App pide coords y
  // PropertyDetail pide el documento en el mismo tick) y el re-abrir la
  // misma propiedad seguido.
  propertyDetail: (id: string, _signal?: AbortSignal) =>
    cachedGet<PropertyDetail>(`/v1/properties/${encodeURIComponent(id)}`, 30_000),
  propertiesList: (params: { filters?: Filters; bbox?: string; limit?: number; offset?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    if (params.bbox) q.set('bbox', params.bbox);
    if (params.filters) {
      for (const [k, v] of Object.entries(filtersToParams(params.filters))) q.set(k, v);
    }
    const qs = q.toString();
    return get<PropertyListResponse>(`/v1/properties${qs ? '?' + qs : ''}`, signal);
  },
  facets: (filters: Filters = {}, _signal?: AbortSignal) => {
    const q = new URLSearchParams(filtersToParams(filters));
    const qs = q.toString();
    // TTL corto: dedupa el par App+FilterBar (disparan el mismo request en
    // el mismo tick por cada cambio de filtros) y el doble-efecto de
    // StrictMode, sin retener facets viejos perceptiblemente.
    return cachedGet<FacetsResponse>(`/v1/facets${qs ? '?' + qs : ''}`, 5_000);
  },
  /**
   * Autocomplete de lugares (estado/municipio/colonia) para el search
   * box principal. Devuelve hasta `limit` sugerencias con `bbox`/`center`
   * para que el cliente haga flyToBounds al seleccionar.
   *
   * El backend descarta queries con menos de 2 chars y devuelve `[]`,
   * pero igualmente cortocircuitamos acá para no quemar request budget.
   */
  places: (q: string, opts: { limit?: number; signal?: AbortSignal } = {}) => {
    const term = q.trim();
    if (term.length < 2) return Promise.resolve({ items: [] } as PlacesResponse);
    const params = new URLSearchParams({ q: term });
    if (opts.limit) params.set('limit', String(opts.limit));
    return get<PlacesResponse>(`/v1/places?${params.toString()}`, opts.signal);
  },
  /**
   * Resumen de criminalidad por municipio (12 meses recientes del SESNSP).
   * Devuelve `null` cuando el dataset no se ha cargado (404/503), para
   * que el llamante no tenga que diferenciar "no hay datos" vs "error
   * real". Errores de red sí se propagan.
   */
  crime: async (cveMun: string, signal?: AbortSignal): Promise<CrimeResponse | null> => {
    const raw = String(cveMun ?? '');
    // Espejo de la validación del backend: dígitos de hasta 5, pad sin truncar.
    if (!/^\d{1,5}$/.test(raw)) return null;
    const safe = raw.padStart(5, '0');
    const t = withTimeout(signal);
    try {
      const res = await fetch(`${BASE}/v1/crime/${safe}`, { signal: t.signal, credentials: 'include' });
      if (res.status === 404 || res.status === 503) return null;
      if (!res.ok) throw new ApiError(res.status, humanHttpError(res.status));
      return (await res.json()) as CrimeResponse;
    } catch (err) {
      if (t.timedOut()) throw new Error(TIMEOUT_MSG);
      throw err;
    } finally {
      t.cleanup();
    }
  },
  /**
   * Alta de alerta por email (RFC-0019): guarda los filtros actuales y
   * el digest diario post-pipeline avisa de inventario nuevo o recortes
   * de precio que matcheen. Devuelve ok=false con `error` legible en
   * 4xx para que el form muestre el mensaje sin lanzar.
   */
  createAlert: async (email: string, filters: Filters): Promise<{ ok: boolean; error?: string; confirmation?: 'sent' | 'pending' | 'already_confirmed' }> => {
    const t = withTimeout(undefined);
    try {
      const res = await fetch(`${BASE}/v1/alerts`, {
        method: 'POST',
        signal: t.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, filters: filtersToParams(filters) }),
      });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; confirmation?: 'sent' | 'pending' | 'already_confirmed' };
      if (res.ok && body.ok) return { ok: true, confirmation: body.confirmation };
      return { ok: false, error: body.error ?? `http_${res.status}` };
    } catch {
      return { ok: false, error: t.timedOut() ? 'timeout' : 'network' };
    } finally {
      t.cleanup();
    }
  },

  // ── Planes y cuenta (RFC-0027) ──────────────────────────────────────
  /** Público: planes vigentes con funciones y cupos. */
  plans: () => cachedGet<{ plans: PublicPlan[] }>('/v1/plans', 300_000),
  /** Con sesión: pide a TerraKairos un cambio de plan (aviso al dueño). */
  accountUpgradeRequest: (plan: string | null, message: string | null) =>
    authFetch<{ ok: boolean }>('/v1/account/upgrade-request', { method: 'POST', body: JSON.stringify({ plan, message }) }),
  /** Público (quien lo usa no puede entrar): pide renovar un plan vencido o reactivar una cuenta. */
  accountRenewalRequest: (email: string) =>
    authFetch<{ ok: boolean }>('/v1/account/renewal-request', { method: 'POST', body: JSON.stringify({ email }) }),
  superSettings: () => authFetch<PlatformSettings>('/v1/superadmin/settings'),
  superUpdateSettings: (patch: { trialSignup: { mode: TrialSignupMode } }) =>
    authFetch<PlatformSettings>('/v1/superadmin/settings', { method: 'PATCH', body: JSON.stringify(patch) }),

  // ── Lista de espera, lado admin ──────────────────────────────────────
  adminAccessRequests: (status = 'PENDING') =>
    authFetch<{ items: AccessRequest[] }>(`/v1/admin/access-requests?status=${encodeURIComponent(status)}`),
  adminApproveAccess: (ids: string[], organizationId?: string) =>
    authFetch<{ approved: number; failed: Array<{ id: string; reason: string }>; items: Array<{ id: string; email: string; emailed: boolean; organizationId: string | null; organizationName: string | null; tempPassword?: string }> }>(
      '/v1/admin/access-requests/approve',
      { method: 'POST', body: JSON.stringify({ ids, organizationId }) },
    ),
  adminRejectAccess: (ids: string[], note?: string) =>
    authFetch<{ rejected: number }>('/v1/admin/access-requests/reject', {
      method: 'POST', body: JSON.stringify({ ids, note }),
    }),

  // ── Auth (RFC-0020) ──────────────────────────────────────────────────
  authMe: () => authFetch<{ user: AuthUser; usage: UsageSummary | null; legal?: LegalStatus | null }>('/v1/auth/me'),
  authAcceptTerms: (version: string) =>
    authFetch<{ ok: boolean; version: string }>('/v1/auth/accept-terms', { method: 'POST', body: JSON.stringify({ version }) }),
  /** Público: ligas a términos y aviso de privacidad, si ya están publicados. */
  legalInfo: () => authFetch<{ enabled: boolean; version?: string; termsUrl?: string; privacyUrl?: string }>('/v1/legal'),
  authLogin: (email: string, password: string) =>
    authFetch<{ user: AuthUser }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  authLogout: () => authFetch<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }),
  authChangePassword: (currentPassword: string, newPassword: string) =>
    authFetch<{ ok: boolean }>('/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  // ── Enlaces de cuenta por correo (RFC-0026) ──────────────────────────
  /** Siempre responde ok (no revela si la cuenta existe). */
  authForgotPassword: (email: string) =>
    authFetch<{ ok: boolean }>('/v1/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  authTokenInspect: (token: string) =>
    authFetch<{ purpose: 'invite' | 'reset'; email: string; name: string | null; expiresAt: string }>(
      '/v1/auth/token/inspect', { method: 'POST', body: JSON.stringify({ token }) },
    ),
  /** Fija la contraseña y deja la sesión abierta (cookie). */
  authTokenComplete: (token: string, newPassword: string) =>
    authFetch<{ user: AuthUser; purpose: 'invite' | 'reset' }>('/v1/auth/token/complete', {
      method: 'POST', body: JSON.stringify({ token, newPassword }),
    }),

  // ── Admin de usuarios (requireAdmin) ─────────────────────────────────
  adminListUsers: (organizationId?: string) =>
    authFetch<{ users: AdminUser[]; organization: OrganizationSeats | null }>(
      `/v1/admin/users${organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ''}`,
    ),
  adminCreateUser: (email: string, name: string | null, role: 'admin' | 'analyst', organizationId?: string) =>
    authFetch<{ user: AdminUser; delivery: AccessDelivery; tempPassword?: string; seats?: { seatLimit: number; seatsUsed: number } }>('/v1/admin/users', {
      method: 'POST',
      body: JSON.stringify({ email, name, role, organizationId }),
    }),

  // ── Super admin: organizaciones y bitácora (RFC-0024) ────────────────
  superMetrics: () => authFetch<SuperMetrics>('/v1/superadmin/metrics'),
  /** Correo de prueba al propio superadmin (valida llave y dominio). */
  superTestEmail: () =>
    authFetch<{ sent: boolean; id: string | null; to: string; from: string }>('/v1/superadmin/test-email', { method: 'POST' }),
  superPlans: () => authFetch<PlansResponse>('/v1/superadmin/plans'),
  superCreatePlan: (input: { code: string; name: string; description?: string | null; defaultSeats?: number; trialDays?: number | null }) =>
    authFetch<{ code: string }>('/v1/superadmin/plans', { method: 'POST', body: JSON.stringify(input) }),
  superUpdatePlan: (code: string, patch: {
    name?: string; description?: string | null; priceMonthlyMxn?: number | null; priceYearlyMxn?: number | null;
    defaultSeats?: number; trialDays?: number | null; isActive?: boolean; sortOrder?: number;
    features?: PlanRules['features']; limits?: PlanRules['limits'];
  }) => authFetch<{ code: string }>(`/v1/superadmin/plans/${encodeURIComponent(code)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  superOrganization: (id: string) => authFetch<OrganizationDetail>(`/v1/superadmin/organizations/${id}`),
  superListOrganizations: () => authFetch<{ organizations: Organization[] }>('/v1/superadmin/organizations'),
  superCreateOrganization: (input: { name: string; plan?: string; seatLimit?: number; activeUntil?: string | null; adminEmail?: string; adminName?: string; notes?: string; contactEmail?: string }) =>
    authFetch<{ organization: Organization; admin: { id: string; email: string; delivery: AccessDelivery; tempPassword?: string } | null }>('/v1/superadmin/organizations', {
      method: 'POST', body: JSON.stringify(input),
    }),
  superUpdateOrganization: (id: string, patch: { name?: string; plan?: string; seatLimit?: number; activeUntil?: string | null; suspended?: boolean; notes?: string; contactEmail?: string | null; overrides?: PlanRules }) =>
    authFetch<{ organization: Organization }>(`/v1/superadmin/organizations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  superAudit: (organizationId?: string, limit = 100) =>
    authFetch<{ items: AuditEntry[] }>(`/v1/superadmin/audit?limit=${limit}${organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : ''}`),
  adminUpdateUser: (id: string, patch: { active?: boolean; role?: 'admin' | 'analyst'; organizationId?: string }) =>
    authFetch<{ user: AdminUser }>(`/v1/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  adminResetPassword: (id: string) =>
    authFetch<{ ok: boolean; email: string; delivery: AccessDelivery; tempPassword?: string }>(`/v1/admin/users/${id}/reset-password`, { method: 'POST' }),
  adminRevokeSessions: (id: string) =>
    authFetch<{ ok: boolean; closed: number }>(`/v1/admin/users/${id}/revoke-sessions`, { method: 'POST' }),
  adminOrganization: (organizationId?: string) =>
    authFetch<MyOrganization>(`/v1/admin/organization${organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ''}`),

  // ── Llaves de API (RFC-0025) ────────────────────────────────────────
  apiKeysList: (organizationId?: string) =>
    authFetch<{ enabled: boolean; monthlyLimit: number | null; keys: ApiKey[] }>(
      `/v1/admin/api-keys${organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ''}`,
    ),
  apiKeysCreate: (name: string, organizationId?: string) =>
    authFetch<{ key: ApiKey; secret: string }>('/v1/admin/api-keys', {
      method: 'POST', body: JSON.stringify({ name, organizationId }),
    }),
  apiKeysRevoke: (id: string) => authFetch<{ ok: boolean }>(`/v1/admin/api-keys/${id}`, { method: 'DELETE' }),

  /**
   * Exportación controlada por plan (RFC-0025): la hace el servidor, que
   * valida el derecho, recorta al cupo mensual y deja bitácora. Devuelve
   * el CSV (ya con BOM) y cuántas filas trae.
   */
  exportCsv: async (filters: Filters, limit = 1000): Promise<{ csv: string; rows: number }> => {
    const q = new URLSearchParams(filtersToParams(filters));
    q.set('limit', String(limit));
    const t = withTimeout(undefined, 120_000);
    try {
      const res = await fetch(`${BASE}/v1/properties/export.csv?${q.toString()}`, { credentials: 'include', signal: t.signal });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        throw new ApiError(res.status, typeof body.error === 'string' ? body.error : `http_${res.status}`, body);
      }
      // `Response.text()` decodifica UTF-8 y QUITA el BOM que manda el
      // servidor; sin él Excel abre el archivo como Latin-1 y rompe los
      // acentos. Se vuelve a anteponer antes de descargar.
      const text = (await res.text()).replace(/^\uFEFF/, '');
      const rows = Math.max(0, text.trim().split(/\r?\n/).length - 1);
      return { csv: '\uFEFF' + text, rows };
    } catch (err) {
      if (t.timedOut()) throw new Error(TIMEOUT_MSG);
      throw err;
    } finally {
      t.cleanup();
    }
  },

  // ── Watchlist por usuario (requireAuth) ──────────────────────────────
  watchlistGet: () => authFetch<{ items: WatchlistItem[]; total: number }>('/v1/watchlist'),
  watchlistAdd: (propertyId: string, note?: string) =>
    authFetch<{ ok: boolean; id: string }>('/v1/watchlist', {
      method: 'POST',
      body: JSON.stringify({ propertyId, note }),
    }),
  watchlistUpdateNote: (propertyId: string, note: string) =>
    authFetch<{ ok: boolean }>(`/v1/watchlist/${propertyId}`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    }),
  watchlistRemove: (propertyId: string) =>
    authFetch<{ ok: boolean }>(`/v1/watchlist/${propertyId}`, { method: 'DELETE' }),

  heatmap: (params: { withGeometry?: boolean; bbox?: string; cveEnt?: string; minScore?: number; limit?: number } = {}, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (params.withGeometry) q.set('geometry', 'true');
    if (params.bbox) q.set('bbox', params.bbox);
    if (params.cveEnt) q.set('cve_ent', params.cveEnt);
    if (params.minScore !== undefined) q.set('min_score', String(params.minScore));
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return get<HeatmapResponse>(`/v1/heatmap${qs ? '?' + qs : ''}`, signal);
  },

  /** Capa socioeconómica por AGEB urbana (RFC-0022). Endpoint público. */
  socioAgebs: (
    params: { withGeometry?: boolean; bbox?: string; cveEnt?: string; grado?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (params.withGeometry) q.set('geometry', 'true');
    if (params.bbox) q.set('bbox', params.bbox);
    if (params.cveEnt) q.set('cve_ent', params.cveEnt);
    if (params.grado) q.set('grado', params.grado);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return get<SocioAgebResponse>(`/v1/socio/agebs${qs ? '?' + qs : ''}`, signal);
  },
};
